import React from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlane, faCar, faTrain, faBus, faSync } from "@fortawesome/free-solid-svg-icons";
import { formatCurrency as formatCost } from "../../utils/currencyFormatter";

const DesktopTransportPackage = ({ tripData, estimateData, onRefresh, isRefreshing }) => {
  const includeTransport = (tripData?.includeTransport ?? estimateData?.includeTransport ?? estimateData?.input?.includeTransport ?? estimateData?.transport?.includeTransport) !== false;
  if (!tripData || !estimateData || !includeTransport) return null;

  const transport = estimateData.transport || {};
  const perLeg = estimateData.transportPerLeg || transport.perLeg || [];
  const transportCost = estimateData.transportTotal || transport.total || 0;

  const pax = estimateData.paxBreakdown || {};
  const a = pax.A || 0;
  const aeb = pax.AEB || 0;
  const cwb = pax.CwB || 0;
  const cnb = pax.CnB || 0;
  const inf = pax.INF || 0;
  const currency = estimateData.currency || "INR";
  const adultCount = a + aeb;
  const childCount = cwb + cnb;

  return (
    <div className="flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm mb-6 lg:mb-8 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faPlane} className="text-teal-600 rotate-45" />
          <div className="flex flex-col">
            <span className="font-bold text-gray-900">Transport Package</span>
            <span className="text-xs text-gray-500">Based on your selected leg modes</span>
          </div>
        </div>
      </div>

      {/* Legs List */}
      <div className="flex flex-col p-6 gap-8">
        {perLeg.length > 0 ? (
          [...perLeg].reverse().map((leg, idx) => {
            const safeMode = (leg.mode || leg.primaryMode || 'UNKNOWN').toUpperCase();
            const isFlight = safeMode === 'FLIGHT' || safeMode === 'FLIGHTS';
            const isTrain = safeMode === 'TRAIN' || safeMode === 'TRAINS';
            const isBus = safeMode === 'BUS' || safeMode === 'BUSES';
            const isDrive = safeMode === 'DRIVE' || safeMode === 'ROAD' || safeMode === 'CAR';
            const icon = isFlight ? faPlane : isTrain ? faTrain : isBus ? faBus : faCar;

            const adultCost = Math.round(leg.costPerAdult || 0);
            const childCost = Math.round(leg.costPerChild || 0);
            const legTotal = (adultCost * adultCount) + (childCost * childCount);

            return (
              <div key={idx} className="flex gap-6 relative">
                {/* Timeline connector */}
                {idx !== perLeg.length - 1 && (
                  <div className="absolute left-4 top-10 bottom-[-32px] w-0.5 bg-gray-100"></div>
                )}

                {/* Icon */}
                <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center z-10 shrink-0 border border-blue-100">
                  <FontAwesomeIcon icon={icon} className="w-3.5 h-3.5" />
                </div>

                {/* Details */}
                <div className="flex-1 flex justify-between gap-2">
                  <div className="flex flex-col min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold text-gray-400 uppercase">LEG {idx + 1} - {leg.type || 'INTERCITY'}</span>
                      <span className="text-[10px] font-bold text-blue-600 uppercase bg-blue-50 px-2 py-0.5 rounded">{safeMode}</span>
                    </div>
                    <span className="font-bold text-gray-900 text-sm mb-4">
                      {leg.from} <span className="text-gray-400 mx-2">→</span> {leg.to}
                    </span>

                    {/* Breakdown per passenger */}
                    {legTotal > 0 && (isFlight || isTrain || isBus || isDrive) ? (
                      <div className="flex flex-col gap-2 ml-4">
                        {adultCount > 0 && (
                          <div className="flex justify-between items-center text-xs text-gray-500 w-full sm:w-64 gap-2">
                            <span className="truncate">{adultCount} Adult {aeb > 0 ? `(Incl. ${aeb} AEB)` : ''}</span>
                            <span className="shrink-0">{formatCost(adultCost, currency)} × {adultCount}</span>
                          </div>
                        )}
                        {childCount > 0 && (
                          <div className="flex justify-between items-center text-xs text-gray-500 w-full sm:w-64 gap-2">
                            <span className="truncate">{childCount} Child (CwB + CnB)</span>
                            <span className="shrink-0">{formatCost(childCost, currency)} × {childCount}</span>
                          </div>
                        )}
                        {inf > 0 && (
                          <div className="flex justify-between items-center text-xs text-gray-400 w-full sm:w-64 gap-2">
                            <span className="truncate">{inf} Infant</span>
                            <span className="shrink-0">{formatCost(0, currency)} × {inf}</span>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>

                  <div className="flex flex-col items-end">
                    {legTotal > 0 ? (
                      <>
                        <span className="font-bold text-gray-900 text-base">{formatCost(legTotal, currency)}</span>
                      </>
                    ) : (isFlight || isTrain || isBus) ? (
                      <>
                        <span className="text-xs font-semibold text-gray-500 uppercase">TBD</span>
                        <span className="text-[10px] text-gray-400 mt-1">price pending</span>
                      </>
                    ) : (
                      <>
                        <span className="text-xs font-semibold text-gray-500 uppercase">Included</span>
                        <span className="text-[10px] text-gray-400 mt-1">under intercity transfers</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-sm text-gray-500 italic py-2">Transport details breakdown not available yet. Refresh pricing to load leg-by-leg data.</div>
        )}
      </div>

      {/* Footer Total */}
      <div className="p-5 border-t border-gray-100 bg-gray-50/50 flex flex-col sm:flex-row justify-between sm:items-center gap-3 sm:gap-0">
        <div className="flex flex-col">
          <span className="text-[10px] font-bold text-gray-500 uppercase">Transport Package Total</span>
          <span className="text-xs text-gray-400 mt-0.5">Updated just now - {perLeg.length} legs - Same for all land packages</span>
        </div>
        <span className="text-2xl font-bold text-teal-600 whitespace-nowrap sm:text-right">
          {formatCost(transportCost, estimateData.currency)}
        </span>
      </div>
    </div>
  );
};

export default DesktopTransportPackage;
